import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { type AgentInboxMessage } from '@/backendApis';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/smoothdown';
import { Time } from '@/components/ui/Time';
import { formatDate } from '@/utils/date';
import { navigate } from '@/utils/navigation-service';

import { AgentInboxDialogHeader } from './agent-inbox-dialog-header';
import { useMessageParties } from './agent-inbox-parties';
import { useAgentInboxMessageQuery } from './agent-inbox.query';

type AgentInboxMessageDetailProps = {
  messageId: string;
  onBack: () => void;
  onOpenChat: () => void;
};

export const AgentInboxMessageDetail = ({
  messageId,
  onBack,
  onOpenChat,
}: AgentInboxMessageDetailProps) => {
  const { data: message, isError } = useAgentInboxMessageQuery(messageId);
  const chatId = message?.chat_id;

  return (
    <>
      <AgentInboxDialogHeader
        title={message ? message.subject || '(no subject)' : 'Email'}
        description={message ? kindLabel(message.kind) : ' '}
        onBack={onBack}
      />
      <ScrollArea className="min-h-0 flex-1">
        <YStack className="gap-5 px-6 pb-6">
          {isError && (
            <p role="alert" className="text-sm text-danger-11">
              Couldn&apos;t load this email.
            </p>
          )}
          {!message && !isError && (
            <YStack className="gap-3">
              <Skeleton className="h-9 w-64" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </YStack>
          )}
          {message && <MessageBody message={message} />}
        </YStack>
      </ScrollArea>
      {chatId && (
        <XStack className="shrink-0 justify-end border-t border-border px-6 py-4">
          <Button
            onClick={() => {
              void navigate({
                to: '/chats/$chatId',
                params: { chatId },
              });
              onOpenChat();
            }}
          >
            Open chat
            <Icons.arrowUpRight className="size-4" />
          </Button>
        </XStack>
      )}
    </>
  );
};

const MessageBody = observer(
  ({ message }: { message: AgentInboxMessage & { body: string } }) => {
    const { counterpart, counterpartName, counterpartLabel } =
      useMessageParties(message);
    return (
      <>
        <XStack className="items-start gap-3">
          {message.kind === 'sent' ? (
            <Box className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-jordy-blue text-brand-midnight-blue">
              <Icons.mail className="size-4" />
            </Box>
          ) : (
            <InitialsAvatar
              name={counterpartName}
              seed={counterpart}
              className="size-9 text-xs"
            />
          )}
          <YStack className="min-w-0 flex-1 gap-0.5">
            <XStack className="items-baseline gap-3">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {message.kind === 'sent' ? 'Your assistant' : counterpartLabel}
              </p>
              <span className="shrink-0 text-xs text-foreground-muted">
                {formatDate(message.created_at, 'MMM d')},{' '}
                <Time date={message.created_at} />
              </span>
            </XStack>
            <p className="truncate text-xs text-foreground-muted">
              {message.kind === 'sent'
                ? counterpartLabel
                : `To ${message.recipients.join(', ')}`}
            </p>
          </YStack>
        </XStack>
        {message.kind === 'held' && (
          <XStack className="gap-2.5 rounded-lg bg-warning-3 px-3 py-2.5 text-sm text-warning-11">
            <Icons.shield className="mt-0.5 size-4 shrink-0" />
            <p>
              Held. This came from someone other than you, so your assistant
              never read or acted on it.
            </p>
          </XStack>
        )}
        {message.kind === 'sent' ? (
          <Markdown id={`agent-inbox-${message.id}`} content={message.body} />
        ) : (
          <p className="whitespace-pre-wrap leading-relaxed text-foreground">
            {message.body}
          </p>
        )}
      </>
    );
  }
);

const kindLabel = (kind: AgentInboxMessage['kind']) =>
  ({
    owner: 'Received',
    signup: 'Sign-up mail',
    held: 'Held',
    sent: 'Sent by your assistant',
  })[kind];
